// Built-in widget localization (en / ko / ru / fr / ar / pt / es).
//
// The widget has always accepted per-key `i18n` overrides, but with no built-in
// dictionary it fell back to English for every unset key — so a Korean visitor
// saw an English UI unless the host wired up every string by hand. This module
// adds a real en/ko dictionary and resolves the visitor's language
// automatically (explicit `locale` option → else navigator.language → English),
// while host overrides still win over the built-in strings.
//
// Two keys are deliberately NOT in the dictionary: `poweredBy` (leaving it unset
// preserves the branded "Powered by <a>Relay</a>" footer + white-label logic)
// and `send` (unset keeps the inline SVG send icon; a value switches it to a
// text label). Both remain host-overridable.

export type WidgetLocale = 'en' | 'ko' | 'ru' | 'fr' | 'ar' | 'pt' | 'es'

/** Right-to-left locales (Arabic) — the widget root sets dir accordingly. */
export function isRTL(locale: WidgetLocale): boolean { return locale === 'ar' }

/** The auto-localized string set (everything except the two special keys above). */
export interface WidgetStrings {
  // conversation view
  placeholder: string
  offline:     string
  online:      string
  away:        string
  aiAssistant: string
  resolved:    string
  reopen:      string
  sources:     string
  helpful:     string
  notHelpful:  string
  translate:   string
  original:    string
  showOriginal: string
  translateUnavailable: string
  // chat list / inbox
  title:       string
  newChat:     string
  startChat:   string
  all:         string
  empty:       string
  error:       string
  retry:       string
  search:      string
  unread:      string
  close:       string
  // accessibility labels (screen readers only — never painted). Localized so a
  // Korean screen-reader user hears Korean; before this they were English
  // literals inside an otherwise localized UI.
  back:         string
  sendMessage:  string
  dismiss:      string
  openChat:     string
  closeChat:    string
  conversation: string
  typing:       string
}

/** Host overrides: any built-in string, plus the two special override-only keys. */
export type WidgetI18nOverrides = Partial<WidgetStrings> & { poweredBy?: string; send?: string }

const EN: WidgetStrings = {
  placeholder: 'Message…',
  offline:     "We're offline right now",
  online:      'Online',
  away:        'Away',
  aiAssistant: 'AI Assistant',
  resolved:    'Marked as resolved',
  reopen:      'Reopen conversation',
  sources:     'Sources',
  helpful:     'Helpful',
  notHelpful:  'Not helpful',
  translate:   'Translate',
  original:    'Original',
  showOriginal: 'Show original',
  translateUnavailable: 'Unavailable',
  title:       'Your conversations',
  newChat:     'New conversation',
  startChat:   'Start a conversation',
  all:         'All conversations',
  empty:       'No conversations yet.',
  error:       'Could not load conversations.',
  retry:       'Retry',
  search:      '🔍  Search',
  unread:      'Unread',
  close:       'Close',
  back:         'Back',
  sendMessage:  'Send message',
  dismiss:      'Dismiss',
  openChat:     'Open chat',
  closeChat:    'Close chat',
  conversation: 'Conversation',
  typing:       'Typing',
}

const KO: WidgetStrings = {
  placeholder: '메시지…',
  offline:     '지금은 오프라인입니다',
  online:      '온라인',
  away:        '자리 비움',
  aiAssistant: 'AI 어시스턴트',
  resolved:    '해결됨으로 표시됨',
  reopen:      '대화 다시 열기',
  sources:     '출처',
  helpful:     '도움돼요',
  notHelpful:  '도움 안돼요',
  translate:   '번역',
  original:    '원문',
  showOriginal: '원문 보기',
  translateUnavailable: '사용할 수 없음',
  title:       '내 대화',
  newChat:     '새 대화',
  startChat:   '대화 시작하기',
  all:         '전체 대화',
  empty:       '아직 대화가 없습니다.',
  error:       '대화를 불러오지 못했습니다.',
  retry:       '다시 시도',
  search:      '🔍  검색',
  unread:      '읽지 않음',
  close:       '닫기',
  back:         '뒤로',
  sendMessage:  '메시지 보내기',
  dismiss:      '닫기',
  openChat:     '채팅 열기',
  closeChat:    '채팅 닫기',
  conversation: '대화',
  typing:       '입력 중',
}

const RU: WidgetStrings = {
  placeholder: 'Сообщение…',
  offline:     'Сейчас мы не в сети',
  online:      'В сети',
  away:        'Нет на месте',
  aiAssistant: 'ИИ-ассистент',
  resolved:    'Отмечено как решённое',
  reopen:      'Возобновить разговор',
  sources:     'Источники',
  helpful:     'Полезно',
  notHelpful:  'Бесполезно',
  translate:   'Перевести',
  original:    'Оригинал',
  showOriginal: 'Показать оригинал',
  translateUnavailable: 'Недоступно',
  title:       'Ваши разговоры',
  newChat:     'Новый разговор',
  startChat:   'Начать разговор',
  all:         'Все разговоры',
  empty:       'Пока нет разговоров.',
  error:       'Не удалось загрузить разговоры.',
  retry:       'Повторить',
  search:      '🔍  Поиск',
  unread:      'Непрочитанное',
  close:       'Закрыть',
  back:         'Назад',
  sendMessage:  'Отправить сообщение',
  dismiss:      'Скрыть',
  openChat:     'Открыть чат',
  closeChat:    'Закрыть чат',
  conversation: 'Беседа',
  typing:       'Печатает',
}

const FR: WidgetStrings = {
  placeholder: 'Message…',
  offline:     'Nous sommes hors ligne pour le moment',
  online:      'En ligne',
  away:        'Absent',
  aiAssistant: 'Assistant IA',
  resolved:    'Marqué comme résolu',
  reopen:      'Rouvrir la conversation',
  sources:     'Sources',
  helpful:     'Utile',
  notHelpful:  'Pas utile',
  translate:   'Traduire',
  original:    'Original',
  showOriginal: "Afficher l'original",
  translateUnavailable: 'Indisponible',
  title:       'Vos conversations',
  newChat:     'Nouvelle conversation',
  startChat:   'Démarrer une conversation',
  all:         'Toutes les conversations',
  empty:       'Aucune conversation pour le moment.',
  error:       'Impossible de charger les conversations.',
  retry:       'Réessayer',
  search:      '🔍  Rechercher',
  unread:      'Non lu',
  close:       'Fermer',
  back:         'Retour',
  sendMessage:  'Envoyer le message',
  dismiss:      'Ignorer',
  openChat:     'Ouvrir le chat',
  closeChat:    'Fermer le chat',
  conversation: 'Conversation',
  typing:       'En train d’écrire',
}

const AR: WidgetStrings = {
  placeholder: 'رسالة…',
  offline:     'نحن غير متصلين حاليًا',
  online:      'متصل',
  away:        'غير متواجد',
  aiAssistant: 'مساعد الذكاء الاصطناعي',
  resolved:    'تم وضع علامة كمحلول',
  reopen:      'إعادة فتح المحادثة',
  sources:     'المصادر',
  helpful:     'مفيد',
  notHelpful:  'غير مفيد',
  translate:   'ترجمة',
  original:    'الأصل',
  showOriginal: 'عرض الأصل',
  translateUnavailable: 'غير متاح',
  title:       'محادثاتك',
  newChat:     'محادثة جديدة',
  startChat:   'بدء محادثة',
  all:         'كل المحادثات',
  empty:       'لا توجد محادثات بعد.',
  error:       'تعذّر تحميل المحادثات.',
  retry:       'إعادة المحاولة',
  search:      '🔍  بحث',
  unread:      'غير مقروء',
  close:       'إغلاق',
  back:         'رجوع',
  sendMessage:  'إرسال الرسالة',
  dismiss:      'تجاهل',
  openChat:     'فتح المحادثة',
  closeChat:    'إغلاق المحادثة',
  conversation: 'المحادثة',
  typing:       'يكتب',
}

const PT: WidgetStrings = {
  placeholder: 'Mensagem…',
  offline:     'Estamos offline no momento',
  online:      'Online',
  away:        'Ausente',
  aiAssistant: 'Assistente de IA',
  resolved:    'Marcado como resolvido',
  reopen:      'Reabrir conversa',
  sources:     'Fontes',
  helpful:     'Útil',
  notHelpful:  'Não útil',
  translate:   'Traduzir',
  original:    'Original',
  showOriginal: 'Mostrar original',
  translateUnavailable: 'Indisponível',
  title:       'Suas conversas',
  newChat:     'Nova conversa',
  startChat:   'Iniciar uma conversa',
  all:         'Todas as conversas',
  empty:       'Ainda não há conversas.',
  error:       'Não foi possível carregar as conversas.',
  retry:       'Tentar novamente',
  search:      '🔍  Pesquisar',
  unread:      'Não lida',
  close:       'Fechar',
  back:         'Voltar',
  sendMessage:  'Enviar mensagem',
  dismiss:      'Dispensar',
  openChat:     'Abrir chat',
  closeChat:    'Fechar chat',
  conversation: 'Conversa',
  typing:       'Digitando',
}

const ES: WidgetStrings = {
  placeholder: 'Mensaje…',
  offline:     'Estamos desconectados en este momento',
  online:      'En línea',
  away:        'Ausente',
  aiAssistant: 'Asistente de IA',
  resolved:    'Marcado como resuelto',
  reopen:      'Reabrir conversación',
  sources:     'Fuentes',
  helpful:     'Útil',
  notHelpful:  'No útil',
  translate:   'Traducir',
  original:    'Original',
  showOriginal: 'Mostrar original',
  translateUnavailable: 'No disponible',
  title:       'Tus conversaciones',
  newChat:     'Nueva conversación',
  startChat:   'Iniciar una conversación',
  all:         'Todas las conversaciones',
  empty:       'Aún no hay conversaciones.',
  error:       'No se pudieron cargar las conversaciones.',
  retry:       'Reintentar',
  search:      '🔍  Buscar',
  unread:      'No leído',
  close:       'Cerrar',
  back:         'Atrás',
  sendMessage:  'Enviar mensaje',
  dismiss:      'Descartar',
  openChat:     'Abrir chat',
  closeChat:    'Cerrar chat',
  conversation: 'Conversación',
  typing:       'Escribiendo',
}

const DICTS: Record<WidgetLocale, WidgetStrings> = { en: EN, ko: KO, ru: RU, fr: FR, ar: AR, pt: PT, es: ES }

/** Resolve a supported locale from an explicit option or the browser. Supports
 *  en/ko/ru/fr/ar/pt/es; anything else resolves to English. Accepts full BCP-47
 *  tags ('ko-KR'), bare codes ('ko'), or language names ('korean'/'français'). */
export function resolveLocale(explicit?: string): WidgetLocale {
  const raw = (explicit ?? (typeof navigator !== 'undefined' ? navigator.language : '') ?? '').toLowerCase()
  if (raw.startsWith('ko') || raw.includes('korean') || raw.includes('한국')) return 'ko'
  if (raw.startsWith('ru') || raw.includes('russ') || raw.includes('русск')) return 'ru'
  if (raw.startsWith('fr') || raw.includes('french') || raw.includes('français') || raw.includes('francais')) return 'fr'
  if (raw.startsWith('ar') || raw.includes('arabic') || raw.includes('عرب')) return 'ar'
  if (raw.startsWith('pt') || raw.includes('portug') || raw.includes('português') || raw.includes('portugues')) return 'pt'
  if (raw.startsWith('es') || raw.includes('spanish') || raw.includes('español') || raw.includes('espanol') || raw.includes('castell')) return 'es'
  return 'en'
}

/** The full string set for the resolved locale, with host per-key overrides
 *  applied on top (overrides win; blank/undefined override values are ignored so
 *  a partial override object never blanks a built-in string). The result may
 *  also carry `poweredBy`/`send` when the host provided them. */
export function resolveStrings(locale?: string, overrides?: WidgetI18nOverrides): WidgetStrings & { poweredBy?: string; send?: string } {
  const base = DICTS[resolveLocale(locale)]
  const clean: Record<string, string> = {}
  for (const [k, v] of Object.entries(overrides ?? {})) if (typeof v === 'string' && v !== '') clean[k] = v
  return { ...base, ...clean }
}
